import {
	CustomFieldDefinition,
	Holiday,
	TimeAccountVacationData,
	User,
	WorkingTime,
	WorkingTimeDateSpan,
	WorkingTimeNotice,
	WorkingTimeRequestStep,
	WorkingTimeType,
	AbstractWorkingTimeRequestResponseModel
} from "@/shared/timr";
import {RequestParam} from "@/shared/api";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {MenuEntryList} from "@/shared/ui/contextMenu";
import {WorkingTimeRequestTableSmall} from "@/features/requests/workingTimeRequestTableSmall";
import {isWorkingTimeDateTimeChangeAllowed, isWorkingTimeTypeAllowedForAddAbsence} from "@/shared/securityHelper";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {WorkingTimeRequestStepsComponent} from "@/features/requests/workingTimeRequestStepsComponent";
import {
	AbsenceCalendarLinksModal,
	type AbsenceCalendarLinksModalProps
} from "./absenceCalendarLinksModal";
import {ModalIsland} from "@/shared/reactIslandModal";
import * as TimeVacationAccountInfo from "@/features/timeaccounts/timeVacationAccountInfo";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {mergeProperties} from "@/shared/utils/misc";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDateRange,
	formatHalfDay,
	getISODate,
	isBeforeDay,
	isSameOrBeforeDay,
	momentToPlainDate,
	parseISODate,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {DateTimeChangeableByUserDayRangeMode} from "@/shared/timrTypes";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {type FullCalendarSelection, FullCalendarSelectionContextMenu} from "@/shared/fullCalendarSelectionContextMenu";

export interface WorkingTimeRequestsBaseConfig {
	users: User[];
	allowedUserIds: number[];
	isGroupLeaderForThemself: boolean;
	workingTimeTypes: WorkingTimeType[];
	workingTimeTypesForNotices?: WorkingTimeType[];
	customFieldDefinitions: CustomFieldDefinition[];
	defaultStatusNumber: number;
	dateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	dateTimeChangeableByUserDayRangeUntil: string;
	absenceCalendarSubscriptionToken?: string | null;
	showTeammemberAbsenceCalendar: boolean;
	showAllAbsenceCalendar: boolean;
	timrUrl: string;
}

export enum FilterParamSource {
	Calendar,
	Table
}

export interface CalendarEventSource {
	// FullCalendar supplies Moment values at runtime; convert with momentToPlainDate before forwarding.
	events: (start: moment.Moment, end: moment.Moment, timezone: string | boolean, callback: (events: FullCalendar.EventObject[]) => void) => void;
}

/**
 * FullCalendar event augmented with our custom payload. `data` is discriminated
 * by `className` at the consumer (cast there); see
 * {@link WorkingTimeRequestsBase.buildPopoverContent}. Subclasses extend the
 * set of accepted `data` shapes.
 */
export interface CalendarEvent extends FullCalendar.EventObject {
	name?: string;
	data?: unknown;
	className: string[];
}

export interface WorkingTimeRange {
	user: User;
	workingTimeType: WorkingTimeType;
	start: string;
	end: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	workingTimes: WorkingTime[];
	substitute?: User;
}

export abstract class WorkingTimeRequestsBase {

	protected static EVENT_CLASS_WORKING_TIME_REQUEST = "fc-event-working-time-request";
	protected static EVENT_CLASS_WORKING_TIME = "fc-event-working-time";
	protected static EVENT_CLASS_HOLIDAY = "fc-event-holiday";
	protected static EVENT_CLASS_SUBSTITUTION = "fc-event-substitution";
	protected static EVENT_CLASS_NOTICE = "fc-event-notice";

	protected static DEFAULT_RECORD_DURATION_MINUTES = 30;

	protected workingTimeDialog: WorkingTimeDialog;
	protected contextMenuEntries: MenuEntryList<AbstractWorkingTimeRequestResponseModel>;
	protected workingTimeRequestTable: WorkingTimeRequestTableSmall;
	protected workingTimeRequestCalendar: JQuery;
	protected selectedWorkingTimeRequest: AbstractWorkingTimeRequestResponseModel;
	protected workingTimeNoticeDialog: WorkingTimeNoticeDialogBridge;
	private calendarSelectionContextMenu?: FullCalendarSelectionContextMenu;

	private pendingRequestsXhr?: JQueryXHR;
	private pendingWorkingTimesXhr?: JQueryXHR;
	private pendingDateSpansXhr?: JQueryXHR;
	protected pendingHolidaysXhr?: JQueryXHR;

	protected constructor(public workingTimeRequestTableUrl: string,
	                      public workingTimeRequestCalendarUrl: string,
	                      public workingTimesUrl: string,
	                      public dateSpansUrl: string,
	                      public holidaysUrl: string,
	                      public showUserInfo: boolean,
	                      public showEditButton: boolean,
	                      public config: WorkingTimeRequestsBaseConfig,
	                      public defaultTableOrder: "asc" | "desc") {
	}

	protected init() {
		WorkingTimeRequestUtil.toggleWorkingTimeTypesByRequestNotAvailableWarning(this.config.workingTimeTypes);
		this.initFilter();
		this.contextMenuEntries = this.createContextMenuItems();
		this.workingTimeRequestTable = this.createTable();
		this.workingTimeRequestCalendar = this.createCalendar();
		this.createProcessingLayer();
		this.initWorkingTimeDialog();
		this.initAbsenceCalendarLinksDialog();
		this.initWorkingTimeNoticeDialog();
	}

	protected initWorkingTimeDialog() {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			userSelectionEnabled: this.config.users !== undefined,
			users: this.config.users,
			allowedUserIds: this.config.allowedUserIds,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => {
			this.reload();
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_REQUEST_CREATED, () => {
			this.reload();
		});
	}

	protected createContextMenuItems(): MenuEntryList<AbstractWorkingTimeRequestResponseModel> {
		// maybe overridden in derived classes
		return [];
	}

	protected createTable(): WorkingTimeRequestTableSmall {
		const workingTimeRequestTable = new WorkingTimeRequestTableSmall({
			element: $(".working-time-requests-table"),
			url: this.workingTimeRequestTableUrl,
			initialFilter: this.buildFilterParams(FilterParamSource.Table),
			orderable: false,
			clampNote: false,
			allowExpand: false,
			contextMenuEntries: this.contextMenuEntries,
			showUserInfo: this.showUserInfo,
			showEditButton: this.showEditButton,
			defaultOrder: this.defaultTableOrder
		});
		$(workingTimeRequestTable).on(WorkingTimeRequestTableSmall.EVENT_SELECT, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.selectedWorkingTimeRequest = workingTimeRequest;
			this.workingTimeRequestCalendar.fullCalendar("gotoDate", parseISODate(workingTimeRequest.start)?.toString());
			this.highlightWorkingTimeRequest(workingTimeRequest, true);
			this.fadeBackUnselectedElements(true);
		}).on(WorkingTimeRequestTableSmall.EVENT_UNSELECT, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.unselectWorkingTimeRequest(workingTimeRequest);
		});
		return workingTimeRequestTable;
	}

	protected createCalendar(): JQuery {
		const options: FullCalendar.Options = mergeProperties({
			defaultView: "month",
			header: {
				left: "title",
				center: "",
				right: "prevYear,prev,today,next,nextYear"
			},
			buttonText: {
				today: messages["locale.today"]
			},
			buttonIcons: {
				close: " fa fa-times",
				prev: " fa fa-chevron-left",
				next: " fa fa-chevron-right",
				prevYear: " fa fa-angle-double-left",
				nextYear: " fa fa-angle-double-right"
			},
			loading: (loadingActive, view) => {
				this.onCalendarLoading(loadingActive, view);
			},
			firstDay: globals.firstDayOfWeek,
			monthNames: globals.monthNames,
			monthNamesShort: globals.monthNamesShort,
			dayNames: globals.dayNames,
			dayNamesShort: globals.dayNamesShort,
			contentHeight: "auto",
			displayEventTime: false,
			eventSources: this.buildCalendarEventSources(),
			viewRender: (view, element) => {
				this.onCalendarViewRender(view, element);
			},
			eventRender: (event: CalendarEvent, element) => {
				this.onCalendarEventRender(event, $(element));
			},
			eventDestroy: (event, element) => {
				element.popover("hide");
			},
			selectable: true,
			select: (start, end, jsEvent) => {
				this.onCalendarSelect(momentToPlainDate(start), momentToPlainDate(end).add({days: -1}), jsEvent);
			}
		}, this.getCustomCalendarConfig());
		return $(".working-time-requests-calendar").fullCalendar(options);
	}

	protected getCustomCalendarConfig(): FullCalendar.Options {
		return {};
	}

	protected buildCalendarEventSources(): CalendarEventSource[] {
		const eventSources: CalendarEventSource[] = [];
		eventSources.push({
			events: (start, end, _timezone, callback) => {
				this.loadRequests(momentToPlainDate(start), momentToPlainDate(end), callback);
			}
		});
		eventSources.push({
			events: (start, end, _timezone, callback) => {
				this.loadWorkingTimes(momentToPlainDate(start), momentToPlainDate(end), callback);
			}
		});
		eventSources.push({
			events: (start, end, _timezone, callback) => {
				this.loadDateSpans(momentToPlainDate(start), momentToPlainDate(end), callback);
			}
		});
		if (SecurityStatus.isHolidayCalendarAllowed()) {
			eventSources.push({
				events: (start, end, _timezone, callback) => {
					this.loadHolidays(momentToPlainDate(start), momentToPlainDate(end), callback);
				}
			});
		}
		return eventSources;
	}

	protected createProcessingLayer() {
		$(".fc-view-container").prepend(`
			<div id="fc-view-container-processing">
				<div class="panel panel-flat border-top-primary border-top-lg">
					<div class="panel-body">
						<i class="icon-spinner2 spinner text-primary"></i> ${messages["fullcalendar.processing"]}
					</div>
				</div>
			</div>`);
	}

	protected initFilter() {
		// maybe overridden in derived classes
	}

	private onCalendarLoading(loadingActive: boolean, _view: FullCalendar.ViewObject) {
		$("#fc-view-container-processing").toggle(loadingActive);
	}

	protected onCalendarSelect(startDate: Temporal.PlainDate, endDate: Temporal.PlainDate, jsEvent?: MouseEvent) {
		this.calendarSelectionContextMenu?.destroy();
		this.calendarSelectionContextMenu = undefined;
		if (this.hasWorkingTimeTypesForNotices() && jsEvent) {
			const selection = {start: startDate, end: endDate};
			this.calendarSelectionContextMenu = new FullCalendarSelectionContextMenu(
				selection,
				jsEvent,
				this.createCalendarSelectionContextMenuItems(),
				() => this.onCalendarSelectionContextMenuClosed()
			);
			this.calendarSelectionContextMenu.show();
		} else {
			const {start, end} = this.getStartEndDateForAddAbsence(startDate, endDate);
			this.addAbsence(start, end);
		}
	}

	protected onCalendarViewRender(view: FullCalendar.ViewObject, element: JQuery) {
		this.updateCalendarToolbar(momentToPlainDate(view.intervalStart), momentToPlainDate(view.intervalEnd));
	}

	protected onCalendarEventRender(event: CalendarEvent, element: JQuery) {
		const content = this.buildPopoverContent(event);
		if (content) {
			element.popover({
				trigger: "hover",
				title: event.name,
				placement: "top",
				html: true,
				content: content.html(),
				template: "<div class=\"popover working-time-request-popover\" role=\"tooltip\"><div class=\"arrow\"></div><div class=\"popover-content\"></div></div>",
				container: "body"
			});
		}
	}

	protected buildPopoverContent(event: CalendarEvent): JQuery {
		if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME_REQUEST) !== -1) {
			return this.buildPopoverContentWorkingTimeRequest(event.data as AbstractWorkingTimeRequestResponseModel);
		} else if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME) !== -1) {
			return this.buildPopoverContentWorkingTime(event.data as WorkingTimeRange);
		} else if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_HOLIDAY) !== -1) {
			return this.buildPopoverContentHoliday(event.data as Holiday[]);
		} else if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_NOTICE) !== -1) {
			return this.buildPopoverContentNotice(event.data as WorkingTimeNotice);
		}
	}

	protected buildCalendarTitleNotice(notice: WorkingTimeNotice): string {
		if (notice.userId === globals.userId) {
			return notice.workingTimeTypeName;
		}
		return notice.userFullname + ": " + notice.workingTimeTypeName;
	}

	protected buildPopoverContentNotice(notice: WorkingTimeNotice): JQuery {
		const content = $("<div>");
		content.append($("<h5>").text(notice.userFullname));
		content.append(
			$("<div>")
				.addClass("flex-row justify-content-space-between mb-10")
				.append($("<span>").text(messages["requests.workingTimeRequestsUser.notice"]))
				.append($("<span>").text(notice.workingTimeTypeName))
		);
		content.append($("<p>").html(formatDateRange(
			parseISODate(notice.start),
			parseISODate(notice.end)
		)));
		return content;
	}

	protected buildPopoverContentWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): JQuery {
		const dateSpanRequest = WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) ? workingTimeRequest : null;
		const requesterOrType = this.showUserInfo
			? workingTimeRequest.user.fullname
			: workingTimeRequest.workingTimeType.name;
		const statusLabel = messages["workingTimeRequestStatus." + workingTimeRequest.status];
		const typeName = this.showUserInfo ? workingTimeRequest.workingTimeType.name : "";
		const dateRangeHtml = WorkingTimeRequestUtil.renderDateRange(workingTimeRequest);

		const content = $(
			`<div>
				<div class="flex-row">
					<div class="flex-column-auto mr-15">
						<h5>${htmlEscape(requesterOrType)}</h5>
					</div>
					<div class="flex-column">
						<h5>
							<span class="label request-status-${workingTimeRequest.status} request-category-${workingTimeRequest.workingTimeType.category}">
								<span>${statusLabel}</span>
							</span>
						</h5>
					</div>
				</div>
				${workingTimeRequest.proofStatus && SecurityStatus.isProofsAllowed() ?
				`<div class="flex-row justify-content-end mb-10">
					<span class="label ${this.showUserInfo ? "" : "ignore"} proof-status status-${workingTimeRequest.proofStatus}">
						<span>${messages["proofStatus." + workingTimeRequest.proofStatus]}</span>
					</span>
				</div>` : ""}
				${WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest) ?
				`<div class="flex-row justify-content-end mb-10">
					<span class="badge badge-notice ml-10">${messages["requests.workingTimeRequestsUser.notice"]}</span>
				</div>` : ""}
				<div class="flex-row mb-10 justify-content-space-between">
					<div class="flex-column mr-15">
						<span>${dateRangeHtml}</span>
					</div>
					<div class="flex-column">
						<span class="text-right">${typeName}</span>
					</div>
				</div>
			</div>`
		);

		this.appendSubstitutePopoverContent(content, dateSpanRequest?.substitute?.fullname);

		if (WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) && workingTimeRequest.approvalSteps) {
			content.append($(`<div class="text-left text-uppercase text-size-small text-grey">${messages["workingTimeRequest.approval"]}</div>`));
			this.appendMultiStepPopoverContent(content, workingTimeRequest.approvalSteps);
		} else if (WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest) && workingTimeRequest.cancellationSteps) {
			content.append($(`<div class="text-left text-uppercase text-size-small text-grey">${messages["workingTimeRequest.cancellation"]}</div>`));
			this.appendMultiStepPopoverContent(content, workingTimeRequest.cancellationSteps);
		}

		if (workingTimeRequest.description) {
			content.append($(`<div class="text-left text-uppercase text-size-small text-grey">${messages["workingTimeRequest.description"]}</div>`));
			content.append($(`<p class="editable-pre-wrapped">${htmlEscape(workingTimeRequest.description)}</p>`));
		}

		if (workingTimeRequest.statusComment) {
			content.append($(`<div class="text-left text-uppercase text-size-small text-grey">${messages["workingTimeRequest.statusComment"]}</div>`));
			content.append($(`<p class="editable-pre-wrapped">${htmlEscape(workingTimeRequest.statusComment)}</p>`));
		}

		return content;
	}

	protected buildPopoverContentWorkingTime(workingTimeRange: WorkingTimeRange): JQuery {
		const content = $("<div>");
		const startDate = parseISODate(workingTimeRange.start);
		const endDate = parseISODate(workingTimeRange.end);
		if (this.showUserInfo) {
			content.append($("<h5>").text(workingTimeRange.user.fullname))
				.append($("<p>").text(workingTimeRange.workingTimeType.name));
		} else {
			content.append($("<h5>").text(workingTimeRange.workingTimeType.name));
		}
		content.append($("<p>").html(formatDateRange(startDate, endDate, workingTimeRange.startHalfDay, workingTimeRange.endHalfDay)));
		this.appendSubstitutePopoverContent(content, workingTimeRange.substitute?.fullname);
		return content;
	}

	protected buildPopoverContentHoliday(holidays: Holiday[]): JQuery {
		const content = $("<div>")
			.append($("<h5>").text(messages["requests.workingTimeRequests.holiday"]));
		holidays.forEach(holiday => {
			const data = $("<p>").text(`${holiday.description} (${holiday.holidayCalendar.description})`);
			if (holiday.halfDay) {
				data.append(formatHalfDay(true));
			}
			content.append(data);
		});
		return content;
	}

	protected appendSubstitutePopoverContent(content: JQuery, substituteFullname?: string): void {
		if (substituteFullname && SecurityStatus.isSubstitutesAllowed()) {
			content.append(
				$("<div>").addClass("flex-row")
					.append($("<p>").addClass("mr-5").text(messages["workingTimeRequest.substitute"]))
					.append($("<p>").text(substituteFullname))
			);
		}
	}

	protected appendMultiStepPopoverContent(content: JQuery, steps: WorkingTimeRequestStep[]): void {
		const stepsComponent = document.createElement("working-time-request-steps") as WorkingTimeRequestStepsComponent;
		stepsComponent.steps = steps;
		content.append(stepsComponent);
		content.append($("<p>"));
	}

	protected buildFilterParams(_filterParamSource: FilterParamSource): RequestParam[] {
		// maybe overridden in derived classes
		return [];
	}

	protected loadRequests(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingRequestsXhr?.abort();
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});
		this.pendingRequestsXhr = $.ajax({
			url: this.workingTimeRequestCalendarUrl,
			data: params
		});
		this.pendingRequestsXhr.then((response: { data: AbstractWorkingTimeRequestResponseModel[] }) => {
			const events: CalendarEvent[] = response.data.map(wtr => ({
				id: wtr.id,
				title: this.buildCalendarTitleRequest(wtr),
				start: wtr.start,
				// Minute-based requests are rendered on their start day only; date-span and notice requests span to end + 1 day (exclusive).
				end: WorkingTimeRequestUtil.isWorkingTimeRequest(wtr) ? wtr.start : getISODate(parseISODate(wtr.end)?.add({days: 1})),
				allDay: true,
				className: [
					WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME_REQUEST,
					"request-status-" + wtr.status,
					"request-category-" + wtr.workingTimeType.category,
					WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(wtr) ? "request-border-dashed" : ""
				].concat(this.selectedWorkingTimeRequest && this.selectedWorkingTimeRequest.id === wtr.id ? ["fc-event-selected"] : []),
				data: wtr
			}));
			callback(events);
		}, () => callback([]));
	}

	protected loadWorkingTimes(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingWorkingTimesXhr?.abort();
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});
		this.pendingWorkingTimesXhr = $.ajax({
			url: this.workingTimesUrl,
			data: params
		});
		this.pendingWorkingTimesXhr.then((response: { data: WorkingTime[] }) => {
			const events = $.map(this.reduceWorkingTimes(response.data), (wtr: WorkingTimeRange) => {
				return {
					id: wtr.workingTimes[0].id,
					title: this.buildCalendarTitleWorkingTime(wtr),
					start: getISODate(parseISODate(wtr.start)),
					end: getISODate(parseISODate(wtr.end)?.add({days: 1})),
					allDay: true,
					className: [
						WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME,
						"fc-event-disabled",
						"request-status-no-request",
						"request-category-" + wtr.workingTimeType.category
					],
					data: wtr
				};
			});
			callback(events);
		}, () => callback([]));
	}

	protected loadDateSpans(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingDateSpansXhr?.abort();
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});

		this.pendingDateSpansXhr = $.ajax({
			url: this.dateSpansUrl,
			data: params
		});
		this.pendingDateSpansXhr.then((dateSpans: WorkingTimeDateSpan[]) => {
			const events: CalendarEvent[] = $.map(dateSpans, (wtds: WorkingTimeDateSpan) => {
				return {
					id: wtds.id,
					title: this.buildCalendarTitleWorkingTimeDateSpan(wtds),
					start: wtds.start,
					end: getISODate(parseISODate(wtds.end)?.add({days: 1})),
					allDay: true,
					className: [
						WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME,
						"request-status-no-request",
						"request-category-" + wtds.workingTimeType.category
					],
					data: wtds
				};
			});

			callback(events);
		}, () => callback([]));
	}

	/**
	 * Reduces a list of working times where contiguous working times are merged together to be able to display them like requests.
	 * Requires a sorted list of working times by user, workingTimeType, start, end
	 * @param workingTimes A sorted list of working times
	 * @return Returns a list of working time ranges where contiguous working times are merged together
	 */
	protected reduceWorkingTimes(workingTimes: WorkingTime[]): WorkingTimeRange[] {
		const reducedList: WorkingTimeRange[] = [];
		for (const workingTime of workingTimes) {
			const previous = reducedList.length > 0 ? reducedList[reducedList.length - 1] : null;
			const previousEnd = parseISOZonedDateTime(previous?.end);
			const requiredNextStart = previousEnd?.add({days: 1});
			const workingTimeStart = parseISOZonedDateTime(workingTime.start);
			// Contiguous only when the next start is the same instant as previousEnd + 1 day.
			const startsRightAfterPrevious = workingTimeStart != null && requiredNextStart != null &&
				Temporal.ZonedDateTime.compare(workingTimeStart, requiredNextStart) === 0;
			if (previous == null || workingTime.user.id !== previous.user.id || workingTime.workingTimeType.id !== previous.workingTimeType.id ||
				!startsRightAfterPrevious || previous.endHalfDay) {
				reducedList.push({
					user: workingTime.user,
					workingTimeType: workingTime.workingTimeType,
					start: workingTime.start,
					end: workingTime.end,
					startHalfDay: workingTime.durationDays === 0.5,
					endHalfDay: false,
					workingTimes: [workingTime]
				});
			} else {
				previous.end = workingTime.end;
				previous.endHalfDay = workingTime.durationDays === 0.5;
				previous.workingTimes.push(workingTime);
			}
		}

		return reducedList;
	}

	protected loadHolidays(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: CalendarEvent[]) => void) {
		this.pendingHolidaysXhr?.abort();
		const params = this.buildFilterParams(FilterParamSource.Calendar);
		params.push({name: "from", value: getISODate(start)});
		params.push({name: "to", value: getISODate(end)});
		this.pendingHolidaysXhr = $.ajax({
			url: this.holidaysUrl,
			data: params
		});
		this.pendingHolidaysXhr.then((response: { data: Holiday[] }) => {
			const reducedHolidays = response.data.reduce((holidayMap: Record<string, Holiday[]>, holiday) => {
				(holidayMap[holiday.date] = holidayMap[holiday.date] || []).push(holiday);
				return holidayMap;
			}, {});
			const events = $.map(reducedHolidays, (holidays: Holiday[]) => {
				return {
					title: this.buildCalendarTitleHoliday(holidays),
					start: holidays[0].date,
					end: getISODate(parseISODate(holidays[0].date)?.add({days: 1})),
					allDay: true,
					className: [
						WorkingTimeRequestsBase.EVENT_CLASS_HOLIDAY,
						"fc-event-disabled",
						"bg-orange-300"
					],
					data: holidays
				};
			});
			callback(events);
		}, () => callback([]));
	}

	protected buildCalendarTitleRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): string {
		// maybe overridden in derived classes
		return "";
	}

	protected buildCalendarTitleWorkingTime(workingTimeRange: WorkingTimeRange): string {
		// maybe overridden in derived classes
		return "";
	}

	protected buildCalendarTitleWorkingTimeDateSpan(workingTimeDateSpan: WorkingTimeDateSpan): string {
		// maybe overridden in derived classes
		return "";
	}

	protected buildCalendarTitleHoliday(holidays: Holiday[]): string {
		return holidays.map(holiday => holiday.description)
			.filter((description, index, array) => array.indexOf(description) === index)
			.reduce((title, description) => {
				return title ? title + ", " + description : description;
			}, "");
	}

	protected reload() {
		this.reloadTable();
		this.reloadCalendar();
	}

	protected showTimeAccountData(timeAccountData: TimeAccountVacationData) {
		TimeVacationAccountInfo.setTimeAccountVacationFieldValues($("#timeVacationAccountInfoPanel"), timeAccountData, true);
	}

	protected addAbsence(start: Temporal.ZonedDateTime | null = null, end: Temporal.ZonedDateTime | null = null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Working Time Requests User - Add Vacation", "Working Time");
	}

	protected getWorkingTimeTypesForAddAbsence(): WorkingTimeType[] {
		return this.config.workingTimeTypes.filter((workingTimeType: WorkingTimeType) => {
			return isWorkingTimeTypeAllowedForAddAbsence(workingTimeType, isWorkingTimeDateTimeChangeAllowed());
		});
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	private highlightWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, highlight: boolean) {
		$.each(this.workingTimeRequestCalendar.fullCalendar("clientEvents", workingTimeRequest.id), (index, event) => {
			const wtr = event.data as AbstractWorkingTimeRequestResponseModel;
			if (wtr) {
				// the class gets removed on clicking the corresponding entry in the small table
				// (re-)add this class on highlighting, because it's necessary to handle the click event to open the dialog
				event.className = [
					WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME_REQUEST,
					"request-status-" + wtr.status,
					"request-category-" + wtr.workingTimeType.category,
					WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(wtr) ? "request-border-dashed" : ""
				].concat(highlight ? ["fc-event-selected"] : []);
				this.workingTimeRequestCalendar.fullCalendar("updateEvent", event);
			}
		});
	}

	private fadeBackUnselectedElements(selectedElement: boolean) {
		$(".fc-day-grid-container").toggleClass("fc-day-grid-fade-back-unselected", selectedElement);
	}

	private reloadTable() {
		this.workingTimeRequestTable.filter(this.buildFilterParams(FilterParamSource.Table));
	}

	protected reloadCalendar() {
		this.calendarSelectionContextMenu?.destroy();
		this.workingTimeRequestCalendar.fullCalendar("refetchEvents");
	}

	protected updateCalendarToolbar(intervalStart: Temporal.PlainDate, intervalEnd: Temporal.PlainDate) {
		const toolbar = $(".working-time-requests-calendar").find(".fc-toolbar");
		toolbar.find(".fc-prev-button").empty()
			.append($("<i>").addClass("fa fa-angle-left"))
			.append(document.createTextNode(" " + globals.monthNamesShort[intervalStart.subtract({months: 1}).month - 1]));
		toolbar.find(".fc-next-button").empty()
			.append(document.createTextNode(globals.monthNamesShort[intervalStart.add({months: 1}).month - 1] + " "))
			.append($("<i>").addClass("fa fa-angle-right"));
		toolbar.find(".fc-prevYear-button").empty()
			.append($("<i>").addClass("fa fa-angle-double-left"))
			.append(document.createTextNode(" " + intervalStart.subtract({years: 1}).year.toString(10)));
		toolbar.find(".fc-nextYear-button").empty()
			.append(document.createTextNode(intervalStart.add({years: 1}).year.toString(10) + " "))
			.append($("<i>").addClass("fa fa-angle-double-right"));
		// Disable the "today" button when the current view already contains today —
		// pressing it would be a no-op.
		const now = Temporal.Now.plainDateISO();
		// intervalEnd is exclusive; today still counts as contained when it is the view's first day.
		const viewContainsToday = isSameOrBeforeDay(intervalStart, now) && isBeforeDay(now, intervalEnd);
		toolbar.find(".fc-today-button").toggleClass("fc-state-disabled", viewContainsToday);
		toolbar.find(".fc-button").click(() => {
			if (this.selectedWorkingTimeRequest) {
				this.unselectWorkingTimeRequest(this.selectedWorkingTimeRequest);
				this.workingTimeRequestTable.deselectTable();
			}
		});

	}

	private unselectWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.highlightWorkingTimeRequest(workingTimeRequest, false);
		this.selectedWorkingTimeRequest = null;
		this.fadeBackUnselectedElements(false);
	}

	private initAbsenceCalendarLinksDialog() {
		const trigger = document.getElementById("showAbsenceCalenderLinkDialog");
		if (trigger == null) {
			return;
		}

		const modal = new ModalIsland<AbsenceCalendarLinksModalProps>(AbsenceCalendarLinksModal, {
			initialToken: this.config.absenceCalendarSubscriptionToken ?? null,
			showTeammemberAbsenceCalendar: this.config.showTeammemberAbsenceCalendar,
			showAllAbsenceCalendar: this.config.showAllAbsenceCalendar,
			timrBaseUrl: this.config.timrUrl,
			// keep the page config in sync, so reopening the dialog shows the token it ends on
			onTokenChange: (token) => {
				this.config.absenceCalendarSubscriptionToken = token;
			}
		});

		trigger.addEventListener("click", () => {
			modal.Open({initialToken: this.config.absenceCalendarSubscriptionToken ?? null});
		});
	}

	private hasWorkingTimeTypesForNotices(): boolean {
		return SecurityStatus.isWorkingTimeNoticesAllowed() && (this.config.workingTimeTypesForNotices?.length ?? 0) > 0;
	}

	private createCalendarSelectionContextMenuItems(): MenuEntryList<FullCalendarSelection> {
		return [{
			label: messages["workingTime.createVacationButton"],
			action: (selection) => {
				const {start, end} = this.getStartEndDateForAddAbsence(selection.start, selection.end);
				this.addAbsence(start, end);
			}
		}, {
			label: messages["workingTimeNotice.addButton"],
			action: (selection) => {
				this.addNotice(selection)
			}
		}];
	}

	private getStartEndDateForAddAbsence(startDate: Temporal.PlainDate, endDate: Temporal.PlainDate): {start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime} {
		const zoneId = Temporal.Now.timeZoneId();
		const startTime = Temporal.Now.plainTimeISO();
		const endTime = startTime.add({minutes: WorkingTimeRequestsBase.DEFAULT_RECORD_DURATION_MINUTES});
		const start = startDate.toZonedDateTime({plainTime: startTime, timeZone: zoneId});
		const end = endDate.toZonedDateTime({plainTime: endTime, timeZone: zoneId});
		return {start, end};
	}

	protected abstract initWorkingTimeNoticeDialog(): void;

	protected abstract addNotice(selection: FullCalendarSelection): void;

	private onCalendarSelectionContextMenuClosed() {
		this.calendarSelectionContextMenu = undefined;
		this.workingTimeRequestCalendar?.fullCalendar("unselect");
	}
}
